import React, { useState, useEffect, useRef } from 'react';
import { ApiClient } from '../../utils/apiClient';
import { useTranslation } from 'react-i18next';

interface SwitcherOrg {
  id: string;
  name: string;
  role: string;
  is_own: boolean;
  is_reseller_client: boolean;
  reseller_type: string | null;
}

export const OrganizationSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<SwitcherOrg[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const response = await ApiClient.get('/resellers/organizations/');
        if (response.ok) {
          const data: SwitcherOrg[] = await response.json();
          setOrganizations(data);

          // Sync the current org's role to localStorage
          const storedOrgId = localStorage.getItem('organization_id');
          if (storedOrgId) {
            const current = data.find(o => o.id === storedOrgId);
            if (current) {
              localStorage.setItem('organization_role', current.role);
              window.dispatchEvent(new CustomEvent('organizationRoleChanged'));
            }
          }
        }
      } catch {
        // silently fail
      }
    };
    loadOrgs();
    setCurrentOrgId(localStorage.getItem('organization_id'));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only show if user has more than 1 org
  if (organizations.length <= 1) {
    return null;
  }

  const currentOrg = organizations.find(o => o.id === currentOrgId);

  const switchOrg = (orgId: string) => {
    localStorage.setItem('organization_id', orgId);
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      localStorage.setItem('organization_role', org.role);
      window.dispatchEvent(new CustomEvent('organizationRoleChanged'));
    }
    setIsOpen(false);
    window.location.reload();
  };

  const ownOrgs = organizations.filter(o => o.is_own);
  const clientOrgs = organizations.filter(o => o.is_reseller_client && !o.is_own);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <span className="material-icons text-base">business</span>
        <span className="max-w-[140px] truncate">{currentOrg?.name || '...'}</span>
        <span className="material-icons text-base">{isOpen ? 'expand_less' : 'expand_more'}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
          {/* Own accounts */}
          {ownOrgs.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase">
                {t('common.myAccount', 'Mein Konto')}
              </div>
              {ownOrgs.map(org => (
                <button
                  key={org.id}
                  onClick={() => switchOrg(org.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 ${
                    org.id === currentOrgId
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <span className="material-icons text-base">person</span>
                  <span className="truncate">{org.name}</span>
                  {org.id === currentOrgId && (
                    <span className="material-icons text-base ml-auto">check</span>
                  )}
                </button>
              ))}
            </>
          )}

          {/* Client accounts */}
          {clientOrgs.length > 0 && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase">
                {t('common.clientAccounts', 'Kundenkonten')}
              </div>
              {clientOrgs.map(org => (
                <button
                  key={org.id}
                  onClick={() => switchOrg(org.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 ${
                    org.id === currentOrgId
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <span className="material-icons text-base">group</span>
                  <span className="truncate">{org.name}</span>
                  {org.id === currentOrgId && (
                    <span className="material-icons text-base ml-auto">check</span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
